import type { FC } from 'react';
import type { IEvaultApi } from './use-evault-api';
import type { IRevaultResult, IVault, IVaultDocument } from './types';

import { useCallback, useState } from 'react';

import { useNotification } from 'hooks';
import { Modal, StatusChip } from 'views/design-system';

import { formatDisplayId } from './derive';
import { EVAULT_API } from './use-evault-api';

export interface IRevaultModalProps {
	api: IEvaultApi;
	/** The CHECKED-OUT record being brought back. */
	document: IVaultDocument;
	/** The vaults this deck has already loaded, for the picker. May be empty. */
	vaults?: Pick<IVault, '_id' | 'name'>[];
	onClose: () => void;
	/** Done and acknowledged: refetch, so the new record appears in the vaulted list. */
	onRevaulted: () => void;
	/** 403 `EVAULT_NOT_ENTITLED` — close and open the add-on instead. */
	onUnentitled: () => void;
}

/**
 * Bring a checked-out original back — as a NEW record (SIM-23 §B5).
 *
 * ONE CALL, so no step machine: a re-vault is a deposit, and there is nothing to verify
 * against between two halves of it. That is also why this modal is narrow where the
 * checkout's is wide — it asks one question (which vault) and reports one answer.
 *
 * THE SEAL VERDICT IS THE POINT OF THE DONE STATE. The interesting thing about bytes
 * that have been outside the vault is whether they still hash to what was sealed at the
 * original deposit, so it is a chip rather than a line of prose. The third outcome is
 * real and is shown as its own: when the retained original could not be read at all the
 * server sends a `sealNote`, and rendering that as NO MATCH would put a tampering
 * finding on a record nobody touched.
 */
export const RevaultModal: FC<IRevaultModalProps> = ({
	api,
	document: doc,
	vaults,
	onClose,
	onRevaulted,
	onUnentitled,
}) => {
	const { successNotification } = useNotification();
	/* '' means "the vault it came from" — the server's own default, so the common case
	   sends no `vaultId` at all rather than echoing back a value this page guessed. */
	const [vaultId, setVaultId] = useState('');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	const [done, setDone] = useState<IRevaultResult | null>(null);

	const revault = useCallback(async () => {
		if (busy) return;
		setBusy(true);
		setError('');

		const result = await api.post<IRevaultResult>(EVAULT_API.VAULT_REVAULTS, {
			documentId: doc._id,
			...(vaultId ? { vaultId } : {}),
		});
		setBusy(false);

		if (result.kind === 'unentitled') {
			onUnentitled();
			return;
		}
		if (result.kind !== 'ok') {
			/* Verbatim: "Already re-vaulted as DOC-…" names the record to go and look at,
			   and a generic fallback would throw that away. */
			setError(result.message);
			return;
		}

		setDone(result.data);
		successNotification(
			`Re-vaulted as ${formatDisplayId(result.data.document.displayId)}`
		);
	}, [api, busy, doc._id, onUnentitled, successNotification, vaultId]);

	const close = useCallback(() => {
		if (busy) return;
		if (done) {
			onRevaulted();
			return;
		}
		onClose();
	}, [busy, done, onClose, onRevaulted]);

	return (
		<Modal
			open
			title={done ? 'Re-vaulted' : 'Re-vault this document'}
			className="evault-modal"
			sub={
				done
					? undefined
					: 'The retained sealed bytes are deposited as a new vaulted record.'
			}
			onClose={close}
			footer={
				done ? (
					<button className="dbtn pri" type="button" onClick={onRevaulted}>
						Done
					</button>
				) : (
					<>
						<button className="dbtn" type="button" disabled={busy} onClick={close}>
							Cancel
						</button>
						<button
							className="dbtn pri"
							type="button"
							disabled={busy}
							onClick={() => void revault()}
						>
							{busy ? 'Re-vaulting…' : 'Re-vault'}
						</button>
					</>
				)
			}
		>
			{!done && (
				<>
					<div className="tchips">
						<span className="vch strt" title={doc.name}>
							{formatDisplayId(doc.displayId)} {doc.name}
						</span>
					</div>

					{/* Offered only when this deck has the vaults in hand. On the single-vault
					    page there is nothing to choose between, and a select with one option
					    would be a control that cannot be used. */}
					{!!vaults?.length && (
						<>
							<div className="tlab">Vault</div>
							<select
								className="range vsel"
								aria-label="Vault"
								value={vaultId}
								disabled={busy}
								onChange={e => setVaultId(e.target.value)}
							>
								<option value="">
									Where it came from{doc.vault?.name ? ` — ${doc.vault.name}` : ''}
								</option>
								{vaults.map(v => (
									<option key={v._id} value={v._id}>
										{v.name}
									</option>
								))}
							</select>
						</>
					)}

					<div className="note">
						The retained sealed bytes are deposited as a new document that links back
						to {formatDisplayId(doc.displayId)}.
					</div>

					{!!error && <div className="merr">{error}</div>}
				</>
			)}

			{done && (
				<>
					<div className="tdone">
						Re-vaulted as {formatDisplayId(done.document.displayId)}.{' '}
						{done.sealNote ? (
							<>
								<StatusChip verdict="ns">SEAL NOT COMPARED</StatusChip> {done.sealNote}
							</>
						) : (
							<>
								<StatusChip verdict={done.sealMatched ? 'ok' : 'rej'}>
									{done.sealMatched ? 'SEAL MATCH' : 'SEAL NO MATCH'}
								</StatusChip>{' '}
								against the prior original.
							</>
						)}
					</div>
					<ul className="dones">
						<li>
							The new record links back to{' '}
							{formatDisplayId(done.priorDocument.displayId)}, which stays checked
							out.
						</li>
						<li>The re-vault has been recorded in the audit trail.</li>
					</ul>
					{done.certificate?.downloadUrl ? (
						<div className="dbtns">
							<a
								className="dbtn"
								href={done.certificate.downloadUrl}
								target="_blank"
								rel="noopener noreferrer"
							>
								Download certificate (PDF)
							</a>
						</div>
					) : (
						<div className="foot frm">
							The certificate could not be issued. Open the new document and use
							Download certificate to retry.
						</div>
					)}
				</>
			)}
		</Modal>
	);
};

export default RevaultModal;
